import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Download, Calendar } from "lucide-react";
import "./Announcements.css";
import { getActiveTrackId } from "../../../utils/session";
import { resolveMediaUrl } from "../../../utils/media";
import { stripMarkdown } from "../../../utils/markdown";
import MarkdownPreview from "../../Common/MarkdownPreview";

const formatRelativeDate = (value) => {
  if (!value) return "Just now";
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const getFirstAttachmentUrl = (attachments = []) => {
  const firstAttachment = (Array.isArray(attachments) ? attachments : []).find((value) => `${value || ""}`.trim());
  if (!firstAttachment) return "";
  return resolveMediaUrl(firstAttachment);
};

const getAttachmentItems = (attachments = []) =>
  (Array.isArray(attachments) ? attachments : [])
    .map((attachment, index) => {
      const rawValue = `${attachment || ""}`.trim();
      if (!rawValue) return null;

      const resolvedUrl = resolveMediaUrl(rawValue);
      const fileName = rawValue.split("/").pop()?.split("?")[0] || `Attachment ${index + 1}`;

      return {
        name: fileName,
        openUrl: resolvedUrl,
      };
    })
    .filter(Boolean);

const Announcements = ({ items = [] }) => {
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const activeTrackId = getActiveTrackId();
  const allAnnouncementsLink = activeTrackId
    ? `/announcements?trackId=${activeTrackId}`
    : "/announcements";
  const attachmentItems = useMemo(
    () => getAttachmentItems(selectedAnnouncement?.attachments),
    [selectedAnnouncement?.attachments]
  );

  return (
    <>
      <div className="widget-container">
        <div className="widget-header-row">
          <h3 className="widget-title">
            <Megaphone size={20} className="widget-icon" />
            Announcements
          </h3>
          <div className="announcement-header-meta d-flex align-items-center gap-2">
            <small className="text-muted announcement-count">{items.length} item(s)</small>
            <Link to={allAnnouncementsLink} className="view-all-link">All Announcements</Link>
          </div>
        </div>

        {!items.length ? (
          <div className="alert alert-light border">No announcements for this track yet.</div>
        ) : (
          <div className="announcement-cards">
            {items.slice(0, 2).map((item) => {
              const firstAttachmentUrl = getFirstAttachmentUrl(item.attachments);
              return (
                <article
                  className="announcement-card announcement-card-button"
                  key={item._id}
                  onClick={() => setSelectedAnnouncement(item)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedAnnouncement(item);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="ann-card-header">
                    <div className="ann-author">
                      <div className="author-avatar is-avatar">IN</div>
                      <div className="author-info">
                        <span className="author-role">Instructor</span>
                        <span className="author-name">Announcement</span>
                        <span className="ann-time">{formatRelativeDate(item.createdAt)}</span>
                      </div>
                    </div>
                    <span className="badge new-badge">NEW</span>
                  </div>

                  <h4 className="ann-title">{item.title}</h4>
                  <p className="ann-body">{stripMarkdown(item.content) || "No content."}</p>

                  {Array.isArray(item.attachments) && item.attachments.length > 0 ? (
                    <div className="attachment">
                      <div className="att-left">
                        <div className="att-icon pdf-icon">
                          <span className="file-type">URL</span>
                        </div>
                        <div className="att-info">
                          <span className="att-name">Attachment</span>
                          <span className="att-size">{item.attachments.length} file(s)</span>
                        </div>
                      </div>
                      {firstAttachmentUrl ? (
                        <a
                          className="download-btn"
                          href={firstAttachmentUrl}
                          target="_blank"
                          rel="noreferrer"
                          download
                          onClick={(event) => event.stopPropagation()}
                          title="Download attachment"
                        >
                          <Download size={16} />
                        </a>
                      ) : (
                        <button className="download-btn" type="button" disabled title="No downloadable attachment">
                          <Download size={16} />
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="calendar-btn">
                      <Calendar size={14} className="cal-icon" />
                      Posted for your track
                    </span>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>

      {selectedAnnouncement ? (
        <div className="announcement-details-backdrop" onClick={() => setSelectedAnnouncement(null)}>
          <div className="announcement-details-card" onClick={(event) => event.stopPropagation()}>
            <header className="announcement-details-head">
              <div>
                <h3>{selectedAnnouncement.title}</h3>
                <small>{new Date(selectedAnnouncement.createdAt).toLocaleString()}</small>
              </div>
              <button type="button" className="announcement-close-btn" onClick={() => setSelectedAnnouncement(null)}>
                Close
              </button>
            </header>

            <section className="announcement-details-section">
              <h4>Content</h4>
              <MarkdownPreview content={selectedAnnouncement.content} emptyText="No content." />
            </section>

            {Array.isArray(selectedAnnouncement.links) && selectedAnnouncement.links.length ? (
              <section className="announcement-details-section">
                <h4>Links</h4>
                <ul className="announcement-links-list">
                  {selectedAnnouncement.links.map((linkValue, index) => {
                    const resolvedLink = resolveMediaUrl(linkValue);
                    return (
                      <li key={`${resolvedLink}-${index}`}>
                        <a href={resolvedLink} target="_blank" rel="noreferrer">{resolvedLink}</a>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}

            {attachmentItems.length ? (
              <section className="announcement-details-section">
                <h4>Attachments</h4>
                <ul className="announcement-links-list">
                  {attachmentItems.map((attachmentItem, index) => (
                    <li key={`${attachmentItem.openUrl || attachmentItem.name}-${index}`}>
                      {attachmentItem.openUrl ? (
                        <a href={attachmentItem.openUrl} target="_blank" rel="noreferrer">{attachmentItem.name}</a>
                      ) : (
                        <span>{attachmentItem.name}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default Announcements;
